import { Fragment, useState, useEffect } from "react"
import Breadcrumb from "../../layout/breadcrumb"
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Table, Spinner, Progress, Badge } from "reactstrap"
import ApexCharts from "react-apexcharts"
import { Book, FileText, MessageCircle, CheckCircle, Clock, Eye, BarChart2, Activity } from "react-feather"
import authAxios from "../../services/Token"
import './Dashboard.css';
import Swal from 'sweetalert2';

const DashboardEstudiante = () => {
  const [stats, setStats] = useState({
    total_cursos: 0,
    total_contenidos: 0,
    contenidos_vistos: 0,
    contenidos_pendientes: 0,
    total_evaluaciones: 0,
    evaluaciones_completadas: 0,
    total_foros: 0,
    foros_respondidos: 0,
    promedio_general: 0,
    porcentaje_progreso: 0
  });

  const [progresoCursos, setProgresoCursos] = useState([]);
  const [actividadReciente, setActividadReciente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    options: {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: true }
      },
      colors: ['#4680ff'],
      stroke: {
        width: 3,
        curve: 'smooth'
      },
      markers: {
        size: 5,
      },
      xaxis: {
        categories: [],
      },
      yaxis: {
        title: {
          text: 'Contenidos Vistos'
        }
      },
      title: {
        text: 'Progreso Mensual',
        align: 'left'
      }
    },
    series: [{
      name: 'Contenidos Vistos',
      data: []
    }]
  });

  const [pieChartData, setPieChartData] = useState({
    series: [0, 0, 0],
    options: {
      chart: {
        type: 'donut',
        height: 350
      },
      labels: ['Contenidos Vistos', 'Evaluaciones', 'Foros Respondidos'],
      colors: ['#4680ff', '#0e9f6e', '#f05252'],
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 300
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      plotOptions: {
        pie: {
          donut: {
            size: '45%'
          }
        }
      }
    }
  });

  // Cargar datos del dashboard del estudiante
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const estudiante_id = localStorage.getItem('cliente_id'); // Ajusta según tu auth

      const dashboardResponse = await authAxios.get("/estudiante/dashboard", {
        params: { estudiante_id }
      });

      if (dashboardResponse.data.success) {
        const dashboardData = dashboardResponse.data.data;
        
        // Actualizar estadísticas
        setStats(dashboardData.estadisticas_generales);
        setProgresoCursos(dashboardData.progreso_cursos);

        // Actualizar gráfico de línea
        if (dashboardData.graficos.progreso_mensual) {
          setChartData(prev => ({
            ...prev,
            options: {
              ...prev.options,
              xaxis: {
                categories: dashboardData.graficos.progreso_mensual.labels
              }
            },
            series: [{
              name: 'Contenidos Vistos',
              data: dashboardData.graficos.progreso_mensual.contenidos_vistos
            }]
          }));
        }

        // Actualizar gráfico de donut
        if (dashboardData.graficos.distribucion_actividades) {
          const distribucion = dashboardData.graficos.distribucion_actividades;
          setPieChartData(prev => ({
            ...prev,
            series: [
              distribucion.contenidos_vistos,
              distribucion.evaluaciones_completadas,
              distribucion.foros_respondidos
            ]
          }));
        }
      }

      // Cargar actividad reciente
      const actividadResponse = await authAxios.get("/estudiante/actividad-reciente", {
        params: { estudiante_id }
      });

      if (actividadResponse.data.success) {
        setActividadReciente(actividadResponse.data.data);
      }

    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos del dashboard',
        confirmButtonColor: '#4680ff'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Cards de estadísticas
  const StatCard = ({ icon: Icon, title, value, color, subtitle, progress }) => (
    <Col xl="3" lg="6" md="6" sm="12" className="mb-4">
      <Card className="card-hover shadow-sm">
        <CardBody>
          <div className="d-flex align-items-center">
            <div className={`avatar avatar-stats p-50 m-0 me-3 bg-light-${color}`}>
              <Icon size={24} className={`text-${color}`} />
            </div>
            <div className="flex-grow-1">
              <h2 className="fw-bolder mb-0">{value}</h2>
              <p className="card-text mb-1">{title}</p>
              {subtitle && <small className="text-muted">{subtitle}</small>}
              {progress !== undefined && (
                <Progress 
                  value={progress} 
                  color={color}
                  className="mt-2"
                  style={{ height: '6px' }}
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  // Función para obtener icono según tipo de actividad
  const getActivityIcon = (tipo) => {
    switch (tipo) {
      case 'contenido':
        return <Eye size={16} className="text-primary" />;
      case 'evaluacion':
        return <FileText size={16} className="text-success" />;
      case 'foro':
        return <MessageCircle size={16} className="text-info" />;
      default:
        return <Activity size={16} />;
    }
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Fragment>
      <Breadcrumb parent="Dashboard" title="Mi Progreso" />
      <Container fluid={true}>
        
        {/* Cards de Estadísticas Principales */}
        <Row className="mb-4">
          <StatCard
            icon={Book}
            title="Total Cursos"
            value={stats.total_cursos}
            color="primary"
            subtitle="Cursos matriculados"
          />
          <StatCard
            icon={Eye}
            title="Contenidos Vistos"
            value={stats.contenidos_vistos}
            color="success"
            subtitle={`${stats.contenidos_pendientes} pendientes`}
            progress={stats.porcentaje_progreso}
          />
          <StatCard
            icon={FileText}
            title="Evaluaciones"
            value={stats.evaluaciones_completadas}
            color="info"
            subtitle={`de ${stats.total_evaluaciones} total`}
          />
          <StatCard
            icon={MessageCircle}
            title="Foros"
            value={stats.foros_respondidos}
            color="warning"
            subtitle={`de ${stats.total_foros} total`}
          />
        </Row>

        <Row>
          {/* Gráfico de Progreso Mensual */}
          <Col xl="8" lg="7" md="12" sm="12">
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  <BarChart2 size={18} className="me-2" />
                  Mi Progreso Mensual
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                    <Spinner color="primary" />
                    <span className="ms-2">Cargando gráfico...</span>
                  </div>
                ) : (
                  <ApexCharts
                    options={chartData.options}
                    series={chartData.series}
                    type="line"
                    height={350}
                  />
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Gráfico de Distribución de Actividades */}
          <Col xl="4" lg="5" md="12" sm="12">
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  <Activity size={18} className="me-2" />
                  Distribución de Actividades
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                    <Spinner color="primary" />
                    <span className="ms-2">Cargando datos...</span>
                  </div>
                ) : (
                  <div>
                    <ApexCharts
                      options={pieChartData.options}
                      series={pieChartData.series}
                      type="donut"
                      height={350}
                    />
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        Total: {pieChartData.series.reduce((a, b) => a + b, 0)} actividades
                      </small>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Progreso por Curso */}
          <Col xl="6" lg="6" md="12" sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Book size={18} className="me-2" />
                  Progreso por Curso
                </h5>
                <Badge color="primary">
                  Promedio: {stats.promedio_general}%
                </Badge>
              </CardHeader>
              <CardBody>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Curso</th>
                      <th>Progreso</th>
                      <th>Contenidos</th>
                      <th>Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <Spinner size="sm" color="primary" /> Cargando...
                        </td>
                      </tr>
                    ) : progresoCursos.length > 0 ? (
                      progresoCursos.map((curso, index) => (
                        <tr key={curso.curso_id || index}>
                          <td>
                            <div>
                              <strong>{curso.curso_nombre}</strong>
                              <br />
                              <small className="text-muted">{curso.curso_codigo}</small>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Progress 
                                value={curso.porcentaje_completado} 
                                color={
                                  curso.porcentaje_completado >= 80 ? 'success' :
                                  curso.porcentaje_completado >= 50 ? 'warning' : 'danger'
                                }
                                style={{ width: '80px', height: '8px' }}
                                className="me-2"
                              />
                              <small>{curso.porcentaje_completado}%</small>
                            </div>
                          </td>
                          <td>
                            <small>
                              {curso.contenidos_vistos}/{curso.total_contenidos}
                            </small>
                            <br />
                            <Badge color="light" className="text-dark">
                              {curso.contenidos_pendientes} pendientes
                            </Badge>
                          </td>
                          <td>
                            <span className={`fw-bold ${
                              curso.promedio_curso >= 80 ? 'text-success' : 
                              curso.promedio_curso >= 60 ? 'text-warning' : 'text-danger'
                            }`}>
                              {curso.promedio_curso}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          No hay cursos matriculados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          {/* Actividad Reciente */}
          <Col xl="6" lg="6" md="12" sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Activity size={18} className="me-2" />
                  Actividad Reciente
                </h5>
                <Button color="primary" size="sm" onClick={fetchDashboardData}>
                  Actualizar
                </Button>
              </CardHeader>
              <CardBody>
                <div className="activity-timeline">
                  {loading ? (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <span className="ms-2">Cargando actividad...</span>
                    </div>
                  ) : actividadReciente.length > 0 ? (
                    actividadReciente.map((actividad, index) => (
                      <div key={index} className="activity-item d-flex align-items-start mb-3">
                        <div className="activity-icon me-3">
                          {getActivityIcon(actividad.tipo)}
                        </div>
                        <div className="activity-content flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-1 fw-bold">{actividad.actividad}</p>
                            <small className="text-muted">
                              {formatDate(actividad.fecha)}
                            </small>
                          </div>
                          <p className="mb-1 text-muted small">{actividad.materia}</p>
                          <Badge color={
                            actividad.tipo === 'contenido' ? 'primary' :
                            actividad.tipo === 'evaluacion' ? 'success' : 'info'
                          }>
                            {actividad.tipo === 'contenido' ? 'Contenido' :
                             actividad.tipo === 'evaluacion' ? 'Evaluación' : 'Foro'}
                          </Badge>
                          {actividad.nota && (
                            <Badge color="warning" className="ms-1">
                              Nota: {actividad.nota}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-4">
                      <Clock size={48} className="mb-2" />
                      <p>No hay actividad reciente</p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Resumen de Métricas */}
        <Row>
          <Col md="4" sm="12">
            <Card className="bg-gradient-primary text-white">
              <CardBody className="text-center">
                <CheckCircle size={48} className="mb-3" />
                <h3>{stats.porcentaje_progreso}%</h3>
                <p className="mb-0">Progreso General</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4" sm="12">
            <Card className="bg-gradient-success text-white">
              <CardBody className="text-center">
                <Eye size={48} className="mb-3" />
                <h3>{stats.contenidos_vistos}</h3>
                <p className="mb-0">Contenidos Completados</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4" sm="12">
            <Card className="bg-gradient-info text-white">
              <CardBody className="text-center">
                <BarChart2 size={48} className="mb-3" />
                <h3>{stats.promedio_general}%</h3>
                <p className="mb-0">Promedio General</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  )
}

export default DashboardEstudiante;